import { useQuery } from "@tanstack/react-query";
import { useMemo, type PropsWithChildren } from "react";
import { useMatch } from "react-router-dom";
import { loadInstructorClientSummary } from "../integrations/supabase/instructor";
import { readPersistedSessionUserId } from "../integrations/supabase/sessionSnapshot";
import { InstructorClientContext } from "./InstructorClientContext";

/**
 * The URL is the only source of truth for the active client: `/instructor/clients/:id/...` means
 * that client is open, anything else means none is. Nothing is persisted, so a reload, a pasted
 * link, and a fresh session all land in exactly the state the address bar says.
 */
export const InstructorClientProvider = ({ children }: PropsWithChildren) => {
  const tabMatch = useMatch("/instructor/clients/:clientUserId/*");
  const bareMatch = useMatch("/instructor/clients/:clientUserId");
  const rawClientUserId = tabMatch?.params.clientUserId ?? bareMatch?.params.clientUserId;
  const clientUserId = rawClientUserId ? rawClientUserId : null;

  // Keyed by the signed-in user as well (read when the client changes), on top of the
  // OwnerContextProvider dropping every user-specific query when the account changes.
  const sessionUserId = useMemo(
    () => (clientUserId ? readPersistedSessionUserId() : null),
    [clientUserId]
  );

  const summaryQuery = useQuery({
    queryKey: ["instructor-client-summary", sessionUserId, clientUserId],
    queryFn: () => loadInstructorClientSummary(clientUserId as string),
    enabled: Boolean(clientUserId),
  });

  const summary = summaryQuery.data ?? null;

  const value = useMemo(
    () => ({
      activeClient: clientUserId
        ? {
            clientUserId,
            displayName:
              summary?.clientUserId === clientUserId ? summary.displayName : "Client",
            summary: summary?.clientUserId === clientUserId ? summary : null,
          }
        : null,
      isLoading: Boolean(clientUserId) && summaryQuery.isLoading,
      isError: Boolean(clientUserId) && summaryQuery.isError,
    }),
    [clientUserId, summary, summaryQuery.isLoading, summaryQuery.isError]
  );

  return (
    <InstructorClientContext.Provider value={value}>{children}</InstructorClientContext.Provider>
  );
};
